import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  AbilityLimitation, 
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED,
  AREA_TYPES,
  ATTACK_TYPES, 
  DAMAGE_TYPES, 
  DURATION_OPTIONS, 
  EFFECT_TYPES, 
  STANDARD_CASTING_TIMES,
  TARGET_TYPES
} from "@dndModels/Constants";
import { 
  AbilityScoreExpandedType,
  AreaType, 
  AttackType, 
  CastingTime, 
  DamageType, 
  DataType, 
  EffectType, 
  TargetType
} from "@dndModels/Types";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import SelectSourceBox from "@dndComponents/select/SelectSourceBox";

interface Props { 
  obj: AbilityLimitation;
  change_time?: number;
  onChange: (changed: AbilityLimitation) => void;
  onDelete?: () => void;
  disabled?: boolean;
}

export default function AbilityLimitInput({ 
  obj,
  change_time = 0,
  onChange,
  onDelete, // I'll add an implementation for this later.
  disabled = false,
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (prev_change_time !== change_time) {
      setChangeTime(change_time);
    }
  }, [prev_change_time, change_time, setChangeTime]);

  const renderDurations = () => {
    const min_duration_num = obj.min_duration === null ? -2 : obj.min_duration;
    const max_duration_num = obj.max_duration === null ? -2 : obj.max_duration;
    const min_durations = max_duration_num < 0 ? DURATION_OPTIONS.filter(o => o.id !== "-1") : DURATION_OPTIONS.filter(o => {
      const numeric = +o.id;
      return numeric !== -1 && numeric <= max_duration_num;
    });
    const max_durations = min_duration_num < 0 ? DURATION_OPTIONS.filter(o => o.id !== "-1") : DURATION_OPTIONS.filter(o => {
      const numeric = +o.id;
      return numeric !== -1 && numeric >= min_duration_num;
    });
    return (
      <>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox 
            name="Min Duration" 
            options={[...min_durations,{ id: "-2", text: "None" }]}
            value={ obj.min_duration === null ? "-2" : `${obj.min_duration}` } 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.min_duration = changed === "-2" ? null : +changed;
              
              onChange(new_obj);
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox 
            name="Max Duration" 
            options={[...max_durations,{ id: "-2", text: "None" }]}
            value={ obj.max_duration === null ? "-2" : `${obj.max_duration}` } 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.max_duration = changed === "-2" ? null : +changed;
              
              onChange(new_obj);
            }} 
          />
        </Grid>
      </>
    );
  }
  
  const renderRanges = () => {
    return (
      <>
        <Grid
          size={{
            xs: 3
          }}>
          <CheckBox
            name="Use Min Range"
            value={ obj.min_range !== null }
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.min_range = changed ? 0 : null;
              
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { obj.min_range !== null &&
            <StringBox 
              name="Min Range" 
              value={ `${obj.min_range}` } 
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.min_range = +changed;
                
                onChange(new_obj);
              }} 
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <CheckBox
            name="Use Max Range"
            value={ obj.max_range !== null }
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.max_range = changed ? 0 : null;
              
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { obj.max_range !== null &&
            <StringBox 
              name="Max Range" 
              value={ `${obj.max_range}` } 
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.max_range = +changed;
                
                onChange(new_obj);
              }} 
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox
            name="Touch"
            value={ obj.touch }
            options={["Ignore","No","Yes"]}
            onChange={(changed: "Yes" | "No" | "Ignore") => {
              const new_obj = obj;
              new_obj.touch = changed;
              
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox
            name="Self"
            value={ obj.self }
            options={["Ignore","No","Yes"]}
            onChange={(changed: "Yes" | "No" | "Ignore") => {
              const new_obj = obj;
              new_obj.self = changed;
              
              onChange(new_obj);
            }}
          />
        </Grid>
      </>
    );
  }
  
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox 
          name="Specific Ability Name" 
          value={obj.name} 
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.name = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox 
          name="Ability Tag" 
          value={obj.ability_tag} 
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.ability_tag = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectSourceBox
          types={obj.source_types}
          ids={obj.source_ids}
          extra_options={["This Item"]}
          onChange={(changed: { types?: string[], ids?: string[] }) => {
            const new_obj = obj;
            new_obj.source_types = changed.types ? changed.types as DataType[] : [];
            new_obj.source_ids = changed.ids ? changed.ids : [];
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Casting Times" 
          options={STANDARD_CASTING_TIMES}
          extra_options={["Special"]}
          values={obj.casting_times} 
          multiple
          disabled={disabled}
          onChange={(changed: CastingTime[]) => {
            const new_obj = obj;
            new_obj.casting_times = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Target Types" 
          options={TARGET_TYPES}
          values={obj.target_types} 
          multiple
          disabled={disabled}
          onChange={(changed: TargetType[]) => {
            const new_obj = obj;
            new_obj.target_types = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Area Types" 
          options={AREA_TYPES}
          values={obj.area_types} 
          multiple
          disabled={disabled}
          onChange={(changed: AreaType[]) => {
            const new_obj = obj;
            new_obj.area_types = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectResourceBox 
          name="Uses Resource"
          values={obj.uses_resource} 
          multiple
          disabled={disabled}
          onChangeValues={(changed: string[]) => {
            const new_obj = obj;
            new_obj.uses_resource = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Attack Types" 
          options={ATTACK_TYPES}
          values={obj.attack_types} 
          multiple
          disabled={disabled}
          onChange={(changed: AttackType[]) => {
            const new_obj = obj;
            new_obj.attack_types = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Effect Types" 
          options={EFFECT_TYPES}
          values={obj.effect_types} 
          multiple
          disabled={disabled}
          onChange={(changed: EffectType[]) => {
            const new_obj = obj;
            new_obj.effect_types = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Damage Types" 
          options={DAMAGE_TYPES}
          values={obj.damage_types} 
          multiple
          disabled={disabled}
          onChange={(changed: DamageType[]) => {
            const new_obj = obj;
            new_obj.damage_types = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          name="Attack Modifiers" 
          options={ABILITY_SCORES_EXPANDED}
          values={obj.attack_mods} 
          multiple
          disabled={disabled}
          onChange={(changed: AbilityScoreExpandedType[]) => {
            const new_obj = obj;
            new_obj.attack_mods = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Ritual" 
          options={["Yes","No","Ignore"]}
          value={obj.ritual} 
          disabled={disabled}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.ritual = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Concentration" 
          options={["Yes","No","Ignore"]}
          value={obj.concentration} 
          disabled={disabled}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.concentration = changed;
            
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox
          name="Twinnable"
          value={ obj.twinnable_1dd_ua7 }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.twinnable_1dd_ua7 = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox
          name="Disease"
          value={ obj.disease }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.disease = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Charm"
          value={ obj.charm }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.charm = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Contaminated"
          value={ obj.contaminated }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.contaminated = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Sight"
          value={ obj.sight }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.sight = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Verbal"
          value={ obj.verbal }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.verbal = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Somatic"
          value={ obj.somatic }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.somatic = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Material"
          value={ obj.material }
          options={["Ignore","No","Yes"]}
          onChange={(changed: "Yes" | "No" | "Ignore") => {
            const new_obj = obj;
            new_obj.material = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      { obj.casting_times.includes("Special") &&
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Special Casting Time"
            value={obj.special_casting_time}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.special_casting_time = changed;
              
              onChange(new_obj);
            }}
          />
        </Grid> 
      }
      { renderDurations() }
      { renderRanges() }
    </Grid>
  );
}
